import { IFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { InputValidationResult, InputValidationResultKind } from "client/Validation/input";
import { useCallback } from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { HttpRequestError } from "client/Api/httpClient";
import { TileEditMode } from "../common";
import { GenericUpdateTileModel } from "client/Api/Workspace/workspace";
import { AppIconModel } from "client_legacy/AppIcon/common";

export interface UpdateTile {
	updateHandler(): void;
}

export type UpdateTileFormData<TTileModel extends GenericUpdateTileModel> = Omit<TTileModel, "id" | "image" | "shouldHideName">
	& {
		image: AppIconModel;
		shouldHideName: SetOptionalModel<boolean>;
	};

export function useUpdateTile<TTileModel extends GenericUpdateTileModel> (
	formHandler: IFormHandler<UpdateTileFormData<TTileModel>>,
	requestHandler: (
		model: TTileModel,
		signal: AbortSignal
	) => Promise<void>,
	isLoading: boolean,
	setIsLoading: (isLoading: boolean) => void,
	mode: TileEditMode,
	onAfterSave: () => void,
	prepareModelForSaving?: (formData: UpdateTileFormData<TTileModel>) => UpdateTileFormData<TTileModel>
): UpdateTile {
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const updateHandler = useCallback(
		async (): Promise<void> => {
			if (mode.kind !== "edit") {
				throw new Error("Cannot update a tile in the wrong mode");
			}

			if(isLoading){
				return;
			}
			try {
				const validationResult: InputValidationResult = await formHandler.validation.validate(globalCancellation);
				if (validationResult.kind === InputValidationResultKind.Invalid) {
					return;
				}
				const formValue: UpdateTileFormData<TTileModel> = prepareModelForSaving?.(formHandler.value) ?? formHandler.value;
				const {
					image,
					shouldHideName,
					...restOfFormValue
				} = formValue;

				const {
					columnSpan,
					rowSpan
				} = formValue.dimension;

				const appModel: TTileModel = {
					...restOfFormValue,
					id: mode.id,
					image: {
						id: image.id,
						type: image.type,
					},
					shouldHideName: columnSpan === 2
						&& rowSpan === 2
							? shouldHideName
							: { isSet: false }
				} as unknown as TTileModel;

				setIsLoading(true);

				await requestHandler(
					appModel,
					globalCancellation
				);

				setIsLoading(false);
				onAfterSave();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					const key: LocalizationKey = e instanceof HttpRequestError && e.statusCode === 403
						? "_YouDontHavePermissionToEditYourWorkspace"
						: "_UpdateTileError";

					alertManager.showError(localize(key));
				}

				console.error(e);
				setIsLoading(false);
			}

		},
		[
			alertManager,
			formHandler,
			globalCancellation,
			isLoading,
			requestHandler,
			setIsLoading,
			mode,
			onAfterSave,
			prepareModelForSaving
		]
	);

	return {
		updateHandler
	};
};
